'use client';

import { create } from 'zustand';
import { api, bootstrapSessionHydration, createSessionHydrator, type SessionHydrationStatus } from '@loamify/shared-utils';
import {
  clearAdminSessionHint,
  markAdminSessionHintActive,
} from '@/lib/admin-session-hint';

export type AdminAuthUser = {
  id: string;
  email: string;
  role?: string;
  capabilities?: string[];
};

interface AuthState {
  user: AdminAuthUser | null;
  hydrated: boolean;
  sessionStatus: SessionHydrationStatus;
  sessionError: unknown | null;
  setAuth: (user: AdminAuthUser | null) => void;
  confirmSession: (user: AdminAuthUser) => Promise<AdminAuthUser>;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
  bootstrapHydration: () => Promise<void>;
  retryHydration: () => Promise<void>;
}

export function canManageMailTemplates(user: AdminAuthUser | null | undefined) {
  return user?.capabilities?.includes('can_manage_mail_templates') ?? false;
}

export function canAccessAdminTools(user: AdminAuthUser | null | undefined) {
  return user?.capabilities?.includes('can_access_admin_tools') ?? false;
}

export function canManageFeatureFlags(user: AdminAuthUser | null | undefined) {
  return user?.capabilities?.includes('can_manage_feature_flags') ?? false;
}

export const useAuth = create<AuthState>((set, get) => {
  const hydrator = createSessionHydrator<AdminAuthUser>({
    load: async () => {
      const res = await api.adminMe();
      return res.user as AdminAuthUser;
    },
    getKnownUser: () => get().user,
    onStateChange: (state) => {
      if (state.status === 'authenticated') {
        api.markSessionActive();
        markAdminSessionHintActive();
      } else if (state.status === 'anonymous') {
        api.markSessionInactive();
        clearAdminSessionHint();
      }
      set({
        user: state.user,
        hydrated: state.status !== 'idle' && state.status !== 'hydrating',
        sessionStatus: state.status,
        sessionError: state.error,
      });
    },
  });

  const hydrate = async () => {
    await hydrator.hydrate();
  };

  const bootstrapHydration = async () => {
    await bootstrapSessionHydration(hydrator);
  };

  const confirmSession = async (user: AdminAuthUser) => {
    api.markSessionActive();
    markAdminSessionHintActive();
    set({
      user,
      hydrated: true,
      sessionStatus: 'authenticated',
      sessionError: null,
    });
    try {
      const response = await api.adminMe();
      const verified = response.user as AdminAuthUser;
      set({
        user: verified,
        hydrated: true,
        sessionStatus: 'authenticated',
        sessionError: null,
      });
      return verified;
    } catch (error) {
      api.markSessionInactive();
      clearAdminSessionHint();
      set({
        user: null,
        hydrated: true,
        sessionStatus: 'anonymous',
        sessionError: error,
      });
      throw error;
    }
  };

  return {
    user: null,
    hydrated: false,
    sessionStatus: 'idle',
    sessionError: null,
    setAuth: (user) => {
      if (user) {
        api.markSessionActive();
        markAdminSessionHintActive();
      } else {
        api.markSessionInactive();
        clearAdminSessionHint();
      }
      set({
        user,
        hydrated: true,
        sessionStatus: user ? 'authenticated' : 'anonymous',
        sessionError: null,
      });
    },
    confirmSession,
    logout: async () => {
      api.markSessionInactive();
      clearAdminSessionHint();
      set({
        user: null,
        hydrated: true,
        sessionStatus: 'anonymous',
        sessionError: null,
      });
      try {
        await api.adminLogout();
      } catch {
        // Explicit logout always clears local identity even if the API is unreachable.
      }
    },
    hydrate,
    bootstrapHydration,
    retryHydration: async () => {
      await hydrator.retry();
    },
  };
});
